import { type Unsubscribe } from '../utils/Unsubscribe';
import {
  canvasSizeEquals,
  computeCanvasSize,
  MAX_PIXEL_RATIO,
  type CanvasSize,
  type CanvasSizeSource,
} from './CanvasSize';

/** Called whenever the surface's layout size or the display's pixel ratio changes. */
export type CanvasResizeListener = (size: CanvasSize) => void;

/** Construction options for {@link CanvasSurface}. */
export interface CanvasSurfaceOptions {
  /** Element the canvas fills; its content box drives the canvas' layout size. */
  readonly container: HTMLElement;
  /** Overrides the default cap on the device pixel ratio. */
  readonly maxPixelRatio?: number;
}

/**
 * Owns a canvas element and keeps its drawing buffer in step with its layout size.
 *
 * This is the boundary between the page and the renderer: it knows about CSS pixels, device pixel
 * ratios and resize events, and it knows nothing about cameras, scenes or world units. The renderer
 * consumes the canvas plus the reported {@link CanvasSize}.
 */
export class CanvasSurface implements CanvasSizeSource {
  readonly #canvas: HTMLCanvasElement;
  readonly #container: HTMLElement;
  readonly #maxPixelRatio: number;
  readonly #listeners = new Set<CanvasResizeListener>();
  readonly #resizeObserver: ResizeObserver;

  #size: CanvasSize;
  #pixelRatioQuery: MediaQueryList | undefined;
  #disposed = false;

  constructor(options: CanvasSurfaceOptions) {
    this.#container = options.container;
    this.#maxPixelRatio = options.maxPixelRatio ?? MAX_PIXEL_RATIO;

    this.#canvas = document.createElement('canvas');
    this.#canvas.style.display = 'block';
    this.#canvas.style.width = '100%';
    this.#canvas.style.height = '100%';
    // Pointer Events must reach us as raw input; the browser's own gestures would eat drags.
    this.#canvas.style.touchAction = 'none';
    this.#container.appendChild(this.#canvas);

    this.#size = this.#measure();
    this.#applySize();

    this.#resizeObserver = new ResizeObserver(() => {
      this.#refresh();
    });
    this.#resizeObserver.observe(this.#container);
    this.#watchPixelRatio();
  }

  /** The canvas element being managed. */
  get canvas(): HTMLCanvasElement {
    return this.#canvas;
  }

  /** The current layout and backing store size. */
  get size(): CanvasSize {
    return this.#size;
  }

  /**
   * Registers a resize listener and returns a function that removes it.
   *
   * The listener is not called on registration; read {@link size} for the current value.
   */
  addResizeListener(listener: CanvasResizeListener): Unsubscribe {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /** Detaches observers and removes the canvas from the document. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#resizeObserver.disconnect();
    this.#pixelRatioQuery?.removeEventListener('change', this.#onPixelRatioChange);
    this.#pixelRatioQuery = undefined;
    this.#listeners.clear();
    this.#canvas.remove();
  }

  #refresh(): void {
    if (this.#disposed) {
      return;
    }
    const next = this.#measure();
    if (canvasSizeEquals(next, this.#size)) {
      return;
    }
    this.#size = next;
    this.#applySize();
    for (const listener of this.#listeners) {
      listener(next);
    }
  }

  #measure(): CanvasSize {
    const bounds = this.#container.getBoundingClientRect();
    return computeCanvasSize(bounds.width, bounds.height, window.devicePixelRatio, this.#maxPixelRatio);
  }

  #applySize(): void {
    this.#canvas.width = this.#size.pixelWidth;
    this.#canvas.height = this.#size.pixelHeight;
  }

  /*
   * A media query is the only reliable notification that devicePixelRatio changed, which happens when
   * the window moves to a monitor with different scaling. Each query fires once, so it is re-armed.
   */
  #watchPixelRatio(): void {
    this.#pixelRatioQuery?.removeEventListener('change', this.#onPixelRatioChange);
    const query = window.matchMedia(`(resolution: ${String(window.devicePixelRatio)}dppx)`);
    query.addEventListener('change', this.#onPixelRatioChange, { once: true });
    this.#pixelRatioQuery = query;
  }

  readonly #onPixelRatioChange = (): void => {
    if (this.#disposed) {
      return;
    }
    this.#watchPixelRatio();
    this.#refresh();
  };
}
